/**
 * FarmAlertRecipients — who is told when something goes wrong on this farm.
 *
 * Controller faults (a site gone offline, a valve that did not open, a run
 * skipped because a node did not answer) go to the people named here and to
 * nobody else, so somebody who works one farm is not woken by another's dead
 * bridge. An empty list is not "nobody": the org's irrigation admins are told
 * instead, and this panel names them, because a blank list reading as silence
 * is how a farm ends up with no cover and nobody noticing.
 *
 * Anyone in the module can see the list; changing it needs the irrigation
 * admin seat (the server enforces the same).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, Plus, RefreshCw, X } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import espnowService, {
  type AlertRecipientUser,
  type FarmAlertRecipients as Recipients,
} from '../../services/espnowIrrigation';
import { errorMessage } from '../../utils/apiResponse';

function displayName(u: AlertRecipientUser): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email;
}

interface Props {
  farmId: number;
  /** Holds the irrigation admin seat, so may change the list. */
  canEdit: boolean;
}

export default function FarmAlertRecipients({ farmId, canEdit }: Props) {
  const [data, setData] = useState<Recipients | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await espnowService.getFarmAlertRecipients(farmId));
    } catch (err) {
      setError(errorMessage(err, 'Could not load who is notified for this farm.'));
    } finally {
      setLoading(false);
    }
  }, [farmId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (userIds: number[]) => {
    setSaving(true);
    setError(null);
    try {
      setData(await espnowService.setFarmAlertRecipients(farmId, userIds));
      setAdding('');
    } catch (err) {
      setError(errorMessage(err, 'Could not save the change.'));
    } finally {
      setSaving(false);
    }
  };

  const onList = useMemo(() => new Set(data?.recipients.map((r) => r.user_id)), [data]);
  const addable = useMemo(
    () => (data?.candidates ?? []).filter((c) => !onList.has(c.user_id)),
    [data, onList]
  );

  const ids = data?.recipients.map((r) => r.user_id) ?? [];

  return (
    <Card>
      <CardContent className="p-4">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
          <Bell className="h-4 w-4 text-blue-500" />
          Who is notified
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Faults on this farm go to these people: a controller going offline, a valve that did not
          open, a run skipped because a node did not answer. Sensor rules on this farm notify them
          too, unless a rule names its own people.
        </p>

        {loading && !data ? (
          <div className="mt-3 flex items-center text-sm text-gray-400">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : data ? (
          <>
            {data.recipients.length > 0 && (
              <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
                {data.recipients.map((r) => (
                  <li key={r.user_id} className="flex items-start gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-medium text-gray-900">{displayName(r)}</p>
                      <p className="break-all text-xs text-gray-500">{r.email}</p>
                      {!r.notified && (
                        <p className="mt-0.5 text-xs text-amber-700">
                          No irrigation seat any more, so not notified.
                        </p>
                      )}
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => void save(ids.filter((id) => id !== r.user_id))}
                        disabled={saving}
                        aria-label={`Stop notifying ${displayName(r)}`}
                        className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}

            {data.using_fallback &&
              (data.fallback.length > 0 ? (
                <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
                  Nobody is assigned to this farm, so its alerts go to the irrigation admins:{' '}
                  <span className="font-medium">{data.fallback.map(displayName).join(', ')}</span>.
                </div>
              ) : (
                <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Nobody is assigned to this farm and nobody holds an irrigation admin seat, so its
                    alerts reach nobody.
                  </span>
                </div>
              ))}

            {canEdit ? (
              addable.length > 0 ? (
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <label htmlFor={`alert-add-${farmId}`} className="sr-only">
                    Person to notify
                  </label>
                  <select
                    id={`alert-add-${farmId}`}
                    value={adding}
                    onChange={(e) => setAdding(e.target.value)}
                    disabled={saving}
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Choose a person…</option>
                    {addable.map((c) => (
                      <option key={c.user_id} value={c.user_id}>
                        {displayName(c)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void save([...ids, Number(adding)])}
                    disabled={!adding || saving}
                    className="flex items-center justify-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    Notify
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-xs text-gray-500">
                  Everyone holding an irrigation seat is already on the list. Only people with an
                  irrigation seat can be notified.
                </p>
              )
            ) : (
              <p className="mt-3 text-xs text-gray-500">An irrigation admin can change who is notified.</p>
            )}
          </>
        ) : null}

        {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      </CardContent>
    </Card>
  );
}
